import { useState, useEffect } from 'react';
import {
  X, Globe, KeyRound, RefreshCw, ExternalLink,
  CheckCircle2, AlertTriangle, ChevronRight, Copy, Eye, EyeOff, Info
} from 'lucide-react';
import { useStore } from '../services/store';

interface Props {
  onClose: () => void;
  onSaved: () => void;
}

type AuthMode = 'auto' | 'master-token' | 'apikey' | 'cookie';
type Step = 'select' | 'master-token-form' | 'apikey-form' | 'cookie-form' | 'working' | 'done' | 'error';

function getToken(): string {
  try { return JSON.parse(localStorage.getItem('studio-store') || '{}')?.state?.token || ''; }
  catch { return ''; }
}

async function apiFetch(path: string, opts: RequestInit = {}) {
  const r = await fetch(path, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}`, ...(opts.headers || {}) },
  });
  return r.json();
}

export default function GeminiAuthDialog({ onClose, onSaved }: Props) {
  const { setGeminiAuthStatus } = useStore();
  const [mode, setMode] = useState<AuthMode>('auto');
  const [step, setStep] = useState<Step>('select');
  const [status, setStatus] = useState<any>(null);

  // Master-token form state
  const [email, setEmail] = useState('');
  const [oauthToken, setOauthToken] = useState('');

  // API key form state
  const [apiKey, setApiKey] = useState('');
  const [showKey, setShowKey] = useState(false);

  // Cookie form state
  const [psid, setPsid] = useState('');
  const [psidts, setPsidts] = useState('');
  const [showPsid, setShowPsid] = useState(false);

  // Working/result
  const [message, setMessage] = useState('');
  const [errorText, setErrorText] = useState('');
  const [embeddedUrl, setEmbeddedUrl] = useState('');

  useEffect(() => {
    apiFetch('/api/gemini/status').then(d => setStatus(d)).catch(() => {});
    apiFetch('/api/gemini/auth/embedded-setup-url').then(d => setEmbeddedUrl(d.url || 'https://accounts.google.com/EmbeddedSetup')).catch(() => {});
  }, []);

  // ── Auto-detect (browser-cookie3) ────────────────────────────────────────
  const handleAutoChromeImport = async () => {
    setStep('working');
    setMessage('Scanning Chrome/Firefox profiles for google.com cookies…');
    try {
      const res = await apiFetch('/api/gemini/auth/browser-cookie3', { method: 'POST' });
      if (res.ok) {
        setStep('done');
        setMessage(`Imported from ${res.source || 'browser'} — Gemini is now connected.`);
        setGeminiAuthStatus('ok');
      } else {
        setStep('error');
        setErrorText(res.error || 'Import failed');
      }
    } catch (e: any) {
      setStep('error');
      setErrorText(e.message);
    }
  };

  // ── Master token exchange ─────────────────────────────────────────────────
  const handleMasterTokenExchange = async () => {
    if (!email.trim() || !oauthToken.trim()) return;
    setStep('working');
    setMessage('Exchanging oauth_token → master token → minting Google cookies…');
    try {
      const res = await apiFetch('/api/gemini/auth/exchange', {
        method: 'POST',
        body: JSON.stringify({ email: email.trim(), oauthToken: oauthToken.trim() }),
      });
      if (res.ok) {
        setStep('done');
        setMessage(`Master token saved for ${email}. Cookies minted — Gemini connected.`);
        setGeminiAuthStatus('ok');
      } else {
        setStep('error');
        setErrorText(res.error || 'Exchange failed');
      }
    } catch (e: any) {
      setStep('error');
      setErrorText(e.message);
    }
  };

  // ── Refresh from stored master token ─────────────────────────────────────
  const handleRefresh = async () => {
    setStep('working');
    setMessage('Re-minting cookies from stored master token…');
    try {
      const res = await apiFetch('/api/gemini/auth/refresh', { method: 'POST' });
      if (res.ok) {
        setStep('done');
        setMessage('Cookies refreshed — Gemini connected.');
        setGeminiAuthStatus('ok');
      } else {
        setStep('error');
        setErrorText(res.error || 'Refresh failed');
      }
    } catch (e: any) {
      setStep('error');
      setErrorText(e.message);
    }
  };

  // ── Cookie (manual PSID paste) ──────────────────────────────────────────
  const handleCookieSave = async () => {
    if (!psid.trim()) return;
    setStep('working');
    setMessage('Saving cookies…');
    try {
      const res = await apiFetch('/api/gemini/auth', {
        method: 'POST',
        body: JSON.stringify({ mode: 'cookies', psid: psid.trim(), psidts: psidts.trim() }),
      });
      if (res.ok) {
        setStep('done');
        setMessage('Personal cookies saved — using your Gemini account now.');
        setGeminiAuthStatus('ok');
      } else {
        setStep('error');
        setErrorText(res.error || 'Failed to save');
      }
    } catch (e: any) {
      setStep('error');
      setErrorText(e.message);
    }
  };

  // ── API key ───────────────────────────────────────────────────────────────
  const handleApiKey = async () => {
    if (!apiKey.trim()) return;
    setStep('working');
    setMessage('Saving API key…');
    try {
      const res = await apiFetch('/api/gemini/auth', {
        method: 'POST',
        body: JSON.stringify({ mode: 'apikey', apiKey: apiKey.trim() }),
      });
      if (res.ok) {
        setStep('done');
        setMessage('API key saved — Gemini connected.');
        setGeminiAuthStatus('ok');
      } else {
        setStep('error');
        setErrorText(res.error || 'Failed to save');
      }
    } catch (e: any) {
      setStep('error');
      setErrorText(e.message);
    }
  };

  const hasMasterToken = status?.has_master_token;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="w-full max-w-xl rounded-xl border border-[#d6bd7c] bg-[#fdf6e9] shadow-2xl">

        {/* Header */}
        <div className="flex items-center justify-between border-b border-[#d6bd7c]/60 px-5 py-4">
          <div>
            <p className="text-sm font-black tracking-[0.06em] text-[#2f2414]">Connect Gemini</p>
            <p className="text-xs text-[#6d5b3f]">
              {status?.configured
                ? `Currently: ${status.mode}${status.email ? ` · ${status.email}` : ''}`
                : 'Not authenticated'}
            </p>
          </div>
          <button onClick={onClose} className="studio-button-quiet rounded-md p-1.5"><X className="w-4 h-4" /></button>
        </div>

        <div className="px-5 py-4 space-y-4">

          {/* ── Working state ── */}
          {step === 'working' && (
            <div className="flex flex-col items-center gap-3 py-6 text-center">
              <RefreshCw className="w-8 h-8 text-[#258cc0] animate-spin" />
              <p className="text-sm font-bold text-[#2f2414]">{message}</p>
            </div>
          )}

          {/* ── Done state ── */}
          {step === 'done' && (
            <div className="flex flex-col items-center gap-3 py-6 text-center">
              <CheckCircle2 className="w-10 h-10 text-[#76a441]" />
              <p className="text-sm font-bold text-[#2f2414]">{message}</p>
              <button onClick={onSaved} className="mt-2 rounded-lg bg-[#476b24] px-6 py-2 text-sm font-bold text-white hover:bg-[#395719] transition-colors">
                Done
              </button>
            </div>
          )}

          {/* ── Error state ── */}
          {step === 'error' && (
            <div className="space-y-3">
              <div className="flex items-start gap-2 rounded-lg border border-[#dc553c]/50 bg-[#dc553c]/10 px-3 py-2.5 text-xs text-[#8c2d1f]">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />{errorText}
              </div>
              <div className="flex gap-2 justify-end">
                <button onClick={() => { setStep('select'); setErrorText(''); }} className="rounded-lg border border-[#d6bd7c] px-3 py-2 text-xs font-bold text-[#6d5b3f] hover:bg-[#e9d6aa]/60">
                  ← Back
                </button>
              </div>
            </div>
          )}

          {/* ── Mode select ── */}
          {step === 'select' && (
            <div className="space-y-2">

              {/* Refresh from master token (always visible) */}
              <div className={`rounded-lg border p-3 ${hasMasterToken ? 'border-[#76a441]/40 bg-[#76a441]/10' : 'border-[#d6bd7c]/40 bg-[#f4e8c9]/40'}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className={`text-xs font-bold mb-0.5 ${hasMasterToken ? 'text-[#476b24]' : 'text-[#6d5b3f]'}`}>
                      {hasMasterToken ? 'Master token stored ✓' : 'Master token not configured'}
                    </p>
                    <p className="text-xs text-[#6d5b3f]">
                      {hasMasterToken
                        ? `Re-mint fresh cookies — no browser needed. ${status?.email ? `(${status.email})` : ''}`
                        : 'Use "Master Token" below to bootstrap once, then refresh any time.'}
                    </p>
                  </div>
                  <button
                    onClick={handleRefresh}
                    disabled={!hasMasterToken}
                    className="shrink-0 flex items-center gap-1.5 rounded-md bg-[#476b24] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#395719] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <RefreshCw className="w-3.5 h-3.5" />Refresh Cookies
                  </button>
                </div>
              </div>

              {/* Option 1: Auto-detect from Chrome */}
              <button
                onClick={() => { setMode('auto'); handleAutoChromeImport(); }}
                className="w-full flex items-start gap-3 rounded-lg border border-[#d6bd7c] bg-[#fff8ec] hover:bg-[#f4e8c9] p-3 text-left transition-colors"
              >
                <Globe className="w-5 h-5 text-[#258cc0] mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs font-bold text-[#2f2414]">Auto-detect from Chrome / Firefox</p>
                  <p className="text-xs text-[#6d5b3f]">Reads cookies directly from your local browser profile. Log into gemini.google.com in Chrome first.</p>
                </div>
                <ChevronRight className="w-4 h-4 text-[#a89470] ml-auto mt-0.5 shrink-0" />
              </button>

              {/* Option 2: Master token (durable, headless) */}
              <button
                onClick={() => { setMode('master-token'); setStep('master-token-form'); }}
                className="w-full flex items-start gap-3 rounded-lg border border-[#d6bd7c] bg-[#fff8ec] hover:bg-[#f4e8c9] p-3 text-left transition-colors"
              >
                <KeyRound className="w-5 h-5 text-[#865916] mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs font-bold text-[#2f2414]">Master Token <span className="text-[#865916]">(recommended)</span></p>
                  <p className="text-xs text-[#6d5b3f]">One-time browser login → durable token that auto-mints fresh cookies forever. Never expires.</p>
                </div>
                <ChevronRight className="w-4 h-4 text-[#a89470] ml-auto mt-0.5 shrink-0" />
              </button>

              {/* Option 3: API key */}
              <button
                onClick={() => { setMode('apikey'); setStep('apikey-form'); }}
                className="w-full flex items-start gap-3 rounded-lg border border-[#d6bd7c] bg-[#fff8ec] hover:bg-[#f4e8c9] p-3 text-left transition-colors"
              >
                <KeyRound className="w-5 h-5 text-[#6d5b3f] mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs font-bold text-[#2f2414]">API Key (aistudio.google.com)</p>
                  <p className="text-xs text-[#6d5b3f]">Standard Gemini API. Also configures gemini CLI in Flow Panel.</p>
                </div>
                <ChevronRight className="w-4 h-4 text-[#a89470] ml-auto mt-0.5 shrink-0" />
              </button>

              {/* Option 4: Manual PSID cookie */}
              <button
                onClick={() => { setMode('cookie'); setStep('cookie-form'); }}
                className="w-full flex items-start gap-3 rounded-lg border border-[#4285f4]/40 bg-[#4285f4]/5 hover:bg-[#4285f4]/10 p-3 text-left transition-colors"
              >
                <Globe className="w-5 h-5 text-[#4285f4] mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs font-bold text-[#2f2414]">Paste Cookie <span className="text-[#4285f4]">(use your personal account)</span></p>
                  <p className="text-xs text-[#6d5b3f]">Paste your __Secure-1PSID from browser DevTools. Uses your own account quota.</p>
                </div>
                <ChevronRight className="w-4 h-4 text-[#a89470] ml-auto mt-0.5 shrink-0" />
              </button>
            </div>
          )}

          {/* ── Master token form ── */}
          {step === 'master-token-form' && (
            <div className="space-y-3">
              <div className="rounded-lg border border-[#d6bd7c]/60 bg-[#fff8ec] p-3 text-xs space-y-2">
                <p className="font-bold text-[#2f2414] flex items-center gap-1.5"><Info className="w-3.5 h-3.5" />How to get your oauth_token (one-time):</p>
                <ol className="list-decimal list-inside space-y-1 text-[#6d5b3f]">
                  <li>Open this URL in Chrome (logged into your Google account):</li>
                </ol>
                <div className="flex items-center gap-2 rounded bg-[#e9d6aa] px-2 py-1.5 font-mono text-[10px] break-all text-[#2f2414]">
                  <span className="flex-1">{embeddedUrl}</span>
                  <button onClick={() => navigator.clipboard.writeText(embeddedUrl)} className="shrink-0 text-[#6d5b3f] hover:text-[#2f2414]">
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                  <a href={embeddedUrl} target="_blank" rel="noopener noreferrer" className="shrink-0 text-[#258cc0]">
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                </div>
                <ol start={2} className="list-decimal list-inside space-y-1 text-[#6d5b3f]">
                  <li>Complete the login flow. After success, look at the final URL.</li>
                  <li>Copy the value of <code className="font-mono bg-[#e9d6aa] px-1 rounded">oauth_token=…</code> from the URL.</li>
                  <li>Paste it below with your email.</li>
                </ol>
              </div>
              <div>
                <label className="block text-xs font-bold text-[#6d5b3f] mb-1">Google account email</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@gmail.com"
                  className="w-full rounded-lg border border-[#d6bd7c] bg-[#fff8ec] px-3 py-2 text-xs text-[#2f2414] placeholder:text-[#b8a07a] focus:outline-none focus:ring-2 focus:ring-[#865916]/30" />
              </div>
              <div>
                <label className="block text-xs font-bold text-[#6d5b3f] mb-1">oauth_token (from URL)</label>
                <input type="password" value={oauthToken} onChange={e => setOauthToken(e.target.value)} placeholder="4/0A…"
                  className="w-full rounded-lg border border-[#d6bd7c] bg-[#fff8ec] px-3 py-2 text-xs font-mono text-[#2f2414] placeholder:text-[#b8a07a] focus:outline-none focus:ring-2 focus:ring-[#865916]/30" />
              </div>
              <div className="flex gap-2 justify-end">
                <button onClick={() => setStep('select')} className="rounded-lg border border-[#d6bd7c] px-3 py-2 text-xs font-bold text-[#6d5b3f] hover:bg-[#e9d6aa]/60">← Back</button>
                <button onClick={handleMasterTokenExchange} disabled={!email.trim() || !oauthToken.trim()}
                  className="flex items-center gap-1.5 rounded-lg bg-[#865916] px-4 py-2 text-xs font-bold text-white hover:bg-[#6d4712] disabled:opacity-40 disabled:cursor-not-allowed">
                  <KeyRound className="w-3.5 h-3.5" />Exchange & Connect
                </button>
              </div>
            </div>
          )}

          {/* ── API key form ── */}
          {step === 'apikey-form' && (
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-bold text-[#6d5b3f] mb-1">Gemini API Key</label>
                <div className="relative">
                  <input type={showKey ? 'text' : 'password'} value={apiKey} onChange={e => setApiKey(e.target.value)} placeholder="AIza…"
                    className="w-full rounded-lg border border-[#d6bd7c] bg-[#fff8ec] px-3 py-2 pr-10 text-xs font-mono text-[#2f2414] placeholder:text-[#b8a07a] focus:outline-none focus:ring-2 focus:ring-[#258cc0]/30" />
                  <button onClick={() => setShowKey(!showKey)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#6d5b3f]">
                    {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="mt-1 text-xs text-[#6d5b3f]">Get one at <a href="https://aistudio.google.com/app/apikey" target="_blank" rel="noopener noreferrer" className="text-[#258cc0] hover:underline inline-flex items-center gap-0.5">aistudio.google.com <ExternalLink className="w-3 h-3" /></a></p>
              </div>
              <div className="flex gap-2 justify-end">
                <button onClick={() => setStep('select')} className="rounded-lg border border-[#d6bd7c] px-3 py-2 text-xs font-bold text-[#6d5b3f] hover:bg-[#e9d6aa]/60">← Back</button>
                <button onClick={handleApiKey} disabled={!apiKey.trim()}
                  className="flex items-center gap-1.5 rounded-lg bg-[#258cc0] px-4 py-2 text-xs font-bold text-white hover:bg-[#1a6a9e] disabled:opacity-40 disabled:cursor-not-allowed">
                  <CheckCircle2 className="w-3.5 h-3.5" />Save
                </button>
              </div>
            </div>
          )}

          {/* ── Cookie paste form ── */}
          {step === 'cookie-form' && (
            <div className="space-y-3">
              <div className="rounded-lg border border-[#4285f4]/30 bg-[#4285f4]/5 p-3 text-xs space-y-2">
                <p className="font-bold text-[#2f2414] flex items-center gap-1.5"><Info className="w-3.5 h-3.5 text-[#4285f4]" />How to get your __Secure-1PSID:</p>
                <ol className="list-decimal list-inside space-y-1 text-[#6d5b3f]">
                  <li>Open <a href="https://gemini.google.com" target="_blank" rel="noopener noreferrer" className="text-[#4285f4] underline">gemini.google.com</a> in Chrome/Edge logged into your account.</li>
                  <li>Press <code className="font-mono bg-[#e9d6aa] px-1 rounded">F12</code> → Application tab → Cookies → google.com</li>
                  <li>Find <code className="font-mono bg-[#e9d6aa] px-1 rounded">__Secure-1PSID</code> and copy its value.</li>
                  <li>Optionally also copy <code className="font-mono bg-[#e9d6aa] px-1 rounded">__Secure-1PSIDTS</code>.</li>
                </ol>
              </div>
              <div>
                <label className="block text-xs font-bold text-[#6d5b3f] mb-1">__Secure-1PSID <span className="text-[#dc553c]">*</span></label>
                <div className="relative">
                  <input
                    type={showPsid ? 'text' : 'password'}
                    value={psid}
                    onChange={e => setPsid(e.target.value)}
                    placeholder="xxx.yyy..."
                    className="w-full rounded-lg border border-[#d6bd7c] bg-[#fff8ec] px-3 py-2 pr-10 text-xs font-mono text-[#2f2414] placeholder:text-[#b8a07a] focus:outline-none focus:ring-2 focus:ring-[#4285f4]/30"
                  />
                  <button onClick={() => setShowPsid(!showPsid)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#6d5b3f]">
                    {showPsid ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-[#6d5b3f] mb-1">__Secure-1PSIDTS <span className="text-[#a89470]">(optional but recommended)</span></label>
                <input
                  type="password"
                  value={psidts}
                  onChange={e => setPsidts(e.target.value)}
                  placeholder="xxx.yyy..."
                  className="w-full rounded-lg border border-[#d6bd7c] bg-[#fff8ec] px-3 py-2 text-xs font-mono text-[#2f2414] placeholder:text-[#b8a07a] focus:outline-none focus:ring-2 focus:ring-[#4285f4]/30"
                />
              </div>
              <div className="flex gap-2 justify-end">
                <button onClick={() => setStep('select')} className="rounded-lg border border-[#d6bd7c] px-3 py-2 text-xs font-bold text-[#6d5b3f] hover:bg-[#e9d6aa]/60">← Back</button>
                <button onClick={handleCookieSave} disabled={!psid.trim()}
                  className="flex items-center gap-1.5 rounded-lg bg-[#4285f4] px-4 py-2 text-xs font-bold text-white hover:bg-[#2a6bd6] disabled:opacity-40 disabled:cursor-not-allowed">
                  <CheckCircle2 className="w-3.5 h-3.5" />Save & Use My Account
                </button>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
